/**
 * Contact Form Component
 * Final page that captures lead info
 */

import { createLogger } from '../core/logger';
import { isTestMode, isInProgressMode } from '../core/config';
import {
  getState,
  getAnswers,
  setFormSubmitting,
  setFormSubmitted,
  getOrCreateLeadId,
  clearProgress
} from '../core/state';
import { submitLead, updateSessionLeadCreated } from '../services/api';
import { trackLead } from '../services/pixels';
import { trackFormStarted, trackLeadSubmitted } from '../services/progress';
import { getAllTrackingData, getFBP, getFBC } from '../services/tracking';
import { FinalPage, Lead, FieldType, GraphicConfig } from '../types';
import { sendSubmit, sendRedirect, sendResize, applyPrefill, isEmbeddedMode } from '../services/embed';

const logger = createLogger('ContactForm');

let container: HTMLElement | null = null;
let finalPageConfig: FinalPage | null = null;
let funnelId: string = '';
let funnelVersion: number = 0;

/**
 * Initialize contact form
 */
export function initContactForm(
  targetContainer: HTMLElement,
  config: FinalPage,
  funnel: { funnelID: string; version: number }
): void {
  container = targetContainer;
  finalPageConfig = config;
  funnelId = funnel.funnelID;
  funnelVersion = funnel.version;
  logger.info('Contact form initialized');
}

/**
 * Render the contact form
 */
export function renderContactForm(): void {
  if (!container || !finalPageConfig) {
    logger.error('Container or config not initialized');
    return;
  }

  const fields = finalPageConfig.fields || ['firstName', 'lastName', 'email', 'phone'];
  const isInProgress = isInProgressMode();

  // Handle image/graphic
  const imageSource = finalPageConfig.image || finalPageConfig.graphic;
  const imageHtml = imageSource ? renderFinalPageImage(imageSource) : '';

  const html = `
    <div class="carousel-slide">
      <div class="contact-form-container">
        ${isInProgress ? `
          <div class="mode-banner mode-banner--in-progress" style="position: relative; margin-bottom: var(--spacing-lg); border-radius: var(--radius-md);">
            <span class="mode-banner-icon">🚧</span>
            <span>In Progress - form works but data won't be saved</span>
          </div>
        ` : ''}

        ${imageHtml}

        <h2 class="contact-form-title">${escapeHtml(finalPageConfig.headline)}</h2>
        ${finalPageConfig.subtitle ? `<p class="contact-form-subtitle">${escapeHtml(finalPageConfig.subtitle)}</p>` : ''}

        <form id="contact-form" class="contact-form" novalidate>
          <div id="contact-form-errors" class="form-errors-container" style="display: none;"></div>

          <!-- Honeypot -->
          <div class="form-honeypot" aria-hidden="true">
            <label for="contact-website">Website</label>
            <input type="text" id="contact-website" name="website" tabindex="-1" autocomplete="off">
          </div>

          ${renderFields(fields)}

          ${finalPageConfig.consentText ? `
            <div class="form-group form-group-checkbox">
              <label class="checkbox-label">
                <input type="checkbox" id="contact-consent" name="consent" ${finalPageConfig.consentRequired !== false ? 'required' : ''}>
                <span class="checkbox-text">${escapeHtml(finalPageConfig.consentText)}</span>
              </label>
            </div>
          ` : ''}

          <button type="submit" id="contact-submit-btn" class="btn btn-primary btn-large btn-block contact-submit-btn">
            ${escapeHtml(finalPageConfig.ctaText || 'Get My Results')}
          </button>

          ${finalPageConfig.disclaimer ? `
            <p class="contact-form-disclaimer">${escapeHtml(finalPageConfig.disclaimer)}</p>
          ` : ''}
        </form>
      </div>
    </div>
  `;

  // Append to carousel
  const carousel = document.getElementById('quiz-carousel');
  if (carousel) {
    carousel.insertAdjacentHTML('beforeend', html);

    // Scroll to form
    setTimeout(() => {
      const slide = carousel.lastElementChild as HTMLElement;
      slide?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
    }, 100);
  }

  // Attach submit handler
  const form = document.getElementById('contact-form');
  form?.addEventListener('submit', handleSubmit);

  // Track form started (first field focus)
  let formStartTracked = false;
  form?.addEventListener('focusin', () => {
    if (!formStartTracked) {
      formStartTracked = true;
      trackFormStarted();
      logger.debug('Form started tracked');
    }
  }, { once: false });

  // Apply any prefilled values from parent (if embedded)
  applyPrefill();

  // Notify parent iframe of content change
  setTimeout(sendResize, 100);

  logger.info('Contact form rendered');
}

/**
 * Render preview CTA slide (replaces contact form in preview mode)
 * Posts message to parent window so the landing page can handle the action
 */
export function renderPreviewCTA(): void {
  const carousel = document.getElementById('quiz-carousel');
  if (!carousel) {
    logger.error('Carousel not found for preview CTA');
    return;
  }

  const html = `
    <div class="carousel-slide">
      <div class="preview-cta-container">
        <div class="preview-cta-icon">🚀</div>
        <h2 class="preview-cta-title">Want to build your own?</h2>
        <p class="preview-cta-subtitle">Create quiz funnels like this one in minutes — no code required.</p>
        <button type="button" id="preview-cta-btn" class="btn btn-primary btn-large btn-block preview-cta-btn">
          Get Started
        </button>
      </div>
    </div>
  `;

  carousel.insertAdjacentHTML('beforeend', html);

  // Scroll to CTA slide
  setTimeout(() => {
    const slide = carousel.lastElementChild as HTMLElement;
    slide?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
  }, 100);

  // Attach click handler — post message to parent for iframe embedding, fallback to sales page
  const btn = document.getElementById('preview-cta-btn');
  btn?.addEventListener('click', () => {
    logger.info('Preview CTA clicked');
    const isInIframe = window.self !== window.top;
    if (isInIframe) {
      try {
        window.parent.postMessage({ type: '1mf-preview-cta', action: 'get-started' }, '*');
      } catch {
        // postMessage failed, fallback to direct navigation
        window.open('https://1minutefunnel.com', '_blank');
      }
    } else {
      // Not in iframe — navigate directly to sales page
      window.open('https://1minutefunnel.com', '_blank');
    }
  });

  // Notify parent iframe of content change
  setTimeout(sendResize, 100);

  logger.info('Preview CTA rendered');
}

/**
 * Render form fields based on config
 */
function renderFields(fields: FieldType[]): string {
  return fields.map(field => {
    switch (field) {
      case 'name':
        return renderField('name', 'Your Name', 'text', 'name', 'John Smith');
      case 'firstName':
        return renderField('firstName', 'First Name', 'text', 'given-name', 'John');
      case 'lastName':
        return renderField('lastName', 'Last Name', 'text', 'family-name', 'Smith');
      case 'email':
        return renderField('email', 'Email Address', 'email', 'email', 'your@email.com');
      case 'phone':
        return renderField('phone', 'Phone Number', 'tel', 'tel', '(555) 123-4567');
      default:
        return '';
    }
  }).join('');
}

/**
 * Render a single form field
 */
function renderField(
  name: string,
  label: string,
  type: string,
  autocomplete: string,
  placeholder: string
): string {
  return `
    <div class="form-group">
      <label for="contact-${name}" class="form-label">${label}</label>
      <input
        type="${type}"
        id="contact-${name}"
        name="${name}"
        class="form-input"
        placeholder="${placeholder}"
        required
        autocomplete="${autocomplete}">
    </div>
  `;
}

/**
 * Handle form submission
 */
async function handleSubmit(e: Event): Promise<void> {
  e.preventDefault();
  logger.info('Form submitted');

  // Check honeypot
  const honeypot = (document.getElementById('contact-website') as HTMLInputElement)?.value;
  if (honeypot) {
    logger.warn('Honeypot triggered - likely bot');
    showSuccess();
    return;
  }

  // Gather values
  const name = getFieldValue('name');
  const firstName = getFieldValue('firstName');
  const lastName = getFieldValue('lastName');
  const email = getFieldValue('email');
  const phone = getFieldValue('phone');
  const consent = (document.getElementById('contact-consent') as HTMLInputElement)?.checked ?? false;

  // Validate
  const errors: string[] = [];

  if (finalPageConfig?.fields.includes('name') && !name) {
    errors.push('Name is required');
  }
  if (finalPageConfig?.fields.includes('firstName') && !firstName) {
    errors.push('First name is required');
  }
  if (finalPageConfig?.fields.includes('lastName') && !lastName) {
    errors.push('Last name is required');
  }
  if (finalPageConfig?.fields.includes('email')) {
    if (!email) {
      errors.push('Email is required');
    } else if (!isValidEmail(email)) {
      errors.push('Please enter a valid email address');
    }
  }
  if (finalPageConfig?.fields.includes('phone')) {
    if (!phone) {
      errors.push('Phone number is required');
    } else if (!isValidPhone(phone)) {
      errors.push('Please enter a valid phone number');
    }
  }
  if (finalPageConfig?.consentText && finalPageConfig.consentRequired !== false && !consent) {
    errors.push('You must agree to continue');
  }

  if (errors.length > 0) {
    showErrors(errors);
    return;
  }

  clearErrors();
  setFormSubmitting(true);
  showLoadingState();

  // Build lead data
  const trackingData = getAllTrackingData();
  const answers = getAnswers();
  const leadId = getOrCreateLeadId();

  const leadData: Lead = {
    leadID: leadId,
    funnelID: funnelId,
    funnelVersion: funnelVersion,
    timestamp: new Date().toISOString(),
    testMode: isTestMode(),

    name: name || null,
    firstName: firstName || null,
    lastName: lastName || null,
    email: email || null,
    phone: phone || null,

    contactConsent: consent,
    consentText: finalPageConfig?.consentText || null,

    answers,

    utmParams: trackingData.utmParams,
    customParams: trackingData.customParams,
    referrerInfo: trackingData.referrerInfo,

    fbc: getFBC(),
    fbp: getFBP(),

    gdprConsent: getState().compliance.gdprConsent,

    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };

  // Submit
  const result = await submitLead(leadData);

  setFormSubmitting(false);

  if (result.success) {
    logger.info('Lead submitted successfully', { leadId });
    setFormSubmitted(leadId);

    // Update session with lead created
    const currentState = getState();
    updateSessionLeadCreated(funnelId, currentState.session.sessionId);

    // Clear saved progress so next visit starts fresh
    clearProgress();

    // Track conversion (pixels)
    trackLead({
      email: email || undefined,
      phone: phone || undefined,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      leadID: leadId
    });

    // Track to server
    trackLeadSubmitted({ leadId });

    // Notify parent iframe (if embedded)
    sendSubmit(true, leadId);

    showSuccess();
  } else {
    logger.error('Lead submission failed', result.error);
    showErrors(['Failed to submit. Please try again.']);
    hideLoadingState();

    // Notify parent iframe of failure (if embedded)
    sendSubmit(false, null, result.error);
  }
}

/**
 * Get field value
 */
function getFieldValue(name: string): string {
  const input = document.getElementById(`contact-${name}`) as HTMLInputElement;
  return input?.value.trim() || '';
}

/**
 * Validate email
 */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Validate phone
 */
function isValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 10;
}

/**
 * Show validation errors
 */
function showErrors(errors: string[]): void {
  const container = document.getElementById('contact-form-errors');
  if (container) {
    container.innerHTML = errors.map(err =>
      `<div class="form-error">${escapeHtml(err)}</div>`
    ).join('');
    container.style.display = 'block';
  }
}

/**
 * Clear errors
 */
function clearErrors(): void {
  const container = document.getElementById('contact-form-errors');
  if (container) {
    container.innerHTML = '';
    container.style.display = 'none';
  }
}

/**
 * Show loading state
 */
function showLoadingState(): void {
  const btn = document.getElementById('contact-submit-btn');
  if (btn) {
    btn.setAttribute('disabled', 'true');
    btn.innerHTML = `<span class="btn-loading-spinner"></span> Submitting...`;
  }
}

/**
 * Hide loading state
 */
function hideLoadingState(): void {
  const btn = document.getElementById('contact-submit-btn');
  if (btn) {
    btn.removeAttribute('disabled');
    btn.textContent = finalPageConfig?.ctaText || 'Get My Results';
  }
}

/**
 * Show success message
 */
function showSuccess(): void {
  const formContainer = document.querySelector('.contact-form-container');
  if (!formContainer || !finalPageConfig) return;

  const hasRedirect = !!finalPageConfig.redirectURL;
  const delay = finalPageConfig.redirectDelay ?? 3;

  formContainer.innerHTML = `
    <div class="contact-success-container">
      <div class="success-icon">✓</div>
      <h2 class="success-title">${escapeHtml(finalPageConfig.successTitle || 'Thank You!')}</h2>
      <p class="success-message">${escapeHtml(finalPageConfig.successMessage || 'Your submission has been received.')}</p>
      ${hasRedirect ? `
        <p class="success-redirect">Redirecting in <span id="countdown">${delay}</span> seconds...</p>
      ` : `
        <button class="btn btn-secondary" onclick="window.location.search = '?reset=true'">Start Over</button>
      `}
    </div>
  `;

  // Notify parent iframe of content change
  setTimeout(sendResize, 100);

  // Handle redirect
  if (hasRedirect && finalPageConfig.redirectURL) {
    let remaining = delay;
    const countdownEl = document.getElementById('countdown');

    const interval = setInterval(() => {
      remaining--;
      if (countdownEl) countdownEl.textContent = String(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        const redirectUrl = buildRedirectURL(finalPageConfig!.redirectURL!);

        // If embedded, tell parent to redirect. Otherwise redirect directly.
        if (isEmbeddedMode()) {
          sendRedirect(redirectUrl);
        } else {
          window.location.href = redirectUrl;
        }
      }
    }, 1000);
  }
}

/**
 * Build redirect URL with UTM params
 */
function buildRedirectURL(baseUrl: string): string {
  try {
    const currentParams = new URLSearchParams(window.location.search);
    const redirectUrl = new URL(baseUrl);
    const excludeParams = ['funnel', 'inprogress', 'test', 'reset', 'log'];

    for (const [key, value] of currentParams.entries()) {
      if (!excludeParams.includes(key) && !redirectUrl.searchParams.has(key)) {
        redirectUrl.searchParams.set(key, value);
      }
    }

    return redirectUrl.toString();
  } catch {
    return baseUrl;
  }
}

/**
 * Render final page image/graphic
 */
function renderFinalPageImage(source: string | GraphicConfig): string {
  // Handle simple string path
  if (typeof source === 'string') {
    return `
      <div class="final-page-image" style="text-align: center; margin-bottom: var(--spacing-lg);">
        <img src="${escapeHtml(source)}" alt="" style="max-width: 100%; height: auto; border-radius: var(--radius-lg);">
      </div>
    `;
  }

  // Handle GraphicConfig object
  const config = source as GraphicConfig;
  const styles: string[] = [];

  if (config.maxWidth) {
    styles.push(`max-width: ${config.maxWidth}`);
  } else {
    styles.push('max-width: 100%');
  }

  if (config.width) {
    styles.push(`width: ${config.width}px`);
  }

  if (config.height) {
    styles.push(`height: ${config.height}px`);
  } else {
    styles.push('height: auto');
  }

  styles.push('border-radius: var(--radius-lg)');

  const align = config.align || 'center';
  const containerStyle = `text-align: ${align}; margin-bottom: var(--spacing-lg);`;

  return `
    <div class="final-page-image" style="${containerStyle}">
      <img src="${escapeHtml(config.path)}" alt="${escapeHtml(config.alt || '')}" style="${styles.join('; ')};">
    </div>
  `;
}

/**
 * Escape HTML
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
